import React, { Component, createRef, lazy } from 'react';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import type { FileResponseItem, ShareSettings } from '../models.ts';
import { PromiseCompletion } from '@classes/PromiseCompletion.ts';
import apiService from '@services/apiService.ts';
import { folderStore } from '@services/folderStore.ts';
import { defaulComparator, stringSearchService } from '@services/StringSearchService.ts';
import { modalService } from '@components/Modal.tsx';
import { ModalButtonType } from '@components/Modal.tsx';

import { PageHeader } from '@components/PageHeader.tsx';
import { Sidebar } from '@components/Sidebar.tsx';
import { ImgLink } from '@components/Images/ImgLink.tsx';
import { ImgUpload } from '@components/Images/ImgUpload.tsx';
import { UploadPanel } from '@components/UploadPanel.tsx';
import { socketService } from '@services/socketService.ts';
import { uploadService } from '@services/uploadService.ts';
import { UrlUploadDialog } from './dialogs/UrlUploadDialog.tsx';
import { CreateFolderDialog } from './dialogs/CreateFolderDialog.tsx';
import { CreateShareSpaceDialog } from './dialogs/CreateShareSpaceDialog.tsx';
import { SetExpirationDialog } from './dialogs/SetExpirationDialog.tsx';
import { CustomizeShareDialog } from './dialogs/CustomizeShareDialog.tsx';
import { AccessCodeDialog } from './dialogs/AccessCodeDialog.tsx';
import { CustomLinkDialog } from './dialogs/CustomLinkDialog.tsx';
import { ImgSearch } from '@components/Images/ImgSearch.tsx';
import { ImgTrash } from '@components/Images/ImgTrash.tsx';
import ContextMenu from '@components/ContextMenu/ContextMenu.tsx';
import { ContextMenuItem } from '@components/ContextMenu/ContextMenuItem.tsx';
import { TrashView } from '@components/TrashView.tsx';
import { FileGridView } from '@components/FileGridView.tsx';
import { FileListView } from '@components/FileListView.tsx';
import { getPreviewableType } from '@root/utils/fileUtils.ts';
import { ImgFolderAdd } from '@components/Images/ImgFolderAdd.tsx';
import { ImgViewGrid } from '@components/Images/ImgViewGrid.tsx';
import { ImgViewList } from '@components/Images/ImgViewList.tsx';
import { ImgHashtag } from '@components/Images/ImgHashtag.tsx';
import { ImgChevronDown } from '@components/Images/ImgChevronDown.tsx';
import { ImgThreeDots } from '@components/Images/ImgThreeDots.tsx';
import { EnterCodeDialog } from './dialogs/EnterCodeDialog.tsx';

const PdfViewerDialog = lazy(() => import('./dialogs/PdfViewerDialog.tsx'));
const TextViewerDialog = lazy(() => import('./dialogs/TextViewerDialog.tsx'));
const MediaPlayerDialog = lazy(() => import('./dialogs/MediaPlayerDialog.tsx'));
const ZipViewerDialog = lazy(() => import('./dialogs/ZipViewerDialog.tsx'));

@observer
class Home extends Component<unknown> {
  _loading = new PromiseCompletion();
  @observable _files: FileResponseItem[] | null = null;
  @observable _viewMode: 'grid' | 'list' /*| 'cards'*/ = localStorage.getItem('viewMode') === 'list' ? 'list' : 'grid';
  @observable _searchTerm: string = '';
  @observable private _currentFolderId: string | null = null;
  @observable private _folderPath: { id: string; name: string }[] = [];
  @observable private _storageInfo: { usedSpace: number; storageLimit: number; filesCount?: number; trashUsedSpace?: number } | null = null;
  @observable private _page: 'files' | 'trash' = 'files';
  @observable private _trashedFiles: FileResponseItem[] | null = null;
  @observable private _removingIds = new Set<string>();
  @observable private _sidebarOpen = false;
  @observable private _searchOpen = false;
  private _uploadPanelRef = createRef<UploadPanel>();
  private _uploadArrowRef = createRef<HTMLButtonElement>();
  private _moreMenuRef = createRef<HTMLButtonElement>();
  private _searchInputRef = createRef<HTMLInputElement>();
  private _reuploadFileRef = createRef<HTMLInputElement>();
  private _reuploadTarget: FileResponseItem | null = null;
  private _unsubUploadComplete?: () => void;

  constructor(props: unknown) {
    super(props);
    makeObservable(this);
  }

  async componentDidMount() {
    document.addEventListener('visibilitychange', this._onDocumentVisibilitychange);
    window.addEventListener('online', this._onOnlineChange);
    window.addEventListener('beforeunload', this._onBeforeUnload);
    window.addEventListener('popstate', this._onPopState);
    socketService.connect();
    socketService.on('background-done', this._onUploadDone);
    socketService.on('file-deleted', this._onFileDeleted);
    this._unsubUploadComplete = uploadService.onUploadComplete(() => this._loadData());

    // Restore page state from URL on initial load
    const urlParams = new URLSearchParams(window.location.search);
    const urlFolderId = urlParams.get('folder');
    if (window.location.pathname === '/trash') {
      runInAction(() => { this._page = 'trash'; });
    } else if (urlFolderId) {
      runInAction(() => {
        this._currentFolderId = urlFolderId;
        this._folderPath = [{ id: urlFolderId, name: 'Folder' }];
      });
    }

    await this._loadData();
    
    document.getElementsByClassName('ui-loader-container')[0]?.remove();
  }

  componentWillUnmount(): void {
    document.removeEventListener('visibilitychange', this._onDocumentVisibilitychange);
    window.removeEventListener('online', this._onOnlineChange);
    window.removeEventListener('beforeunload', this._onBeforeUnload);
    window.removeEventListener('popstate', this._onPopState);
    socketService.off('background-done', this._onUploadDone);
    socketService.off('file-deleted', this._onFileDeleted);
    this._unsubUploadComplete?.();
  }

  private _onBeforeUnload = (e: BeforeUnloadEvent) => {
    if (uploadService.hasActiveUploads) {
      e.preventDefault();
      e.returnValue = '';
    }
  };

  @action
  private _onDocumentVisibilitychange = () => {
    if (document.visibilityState === 'visible') {
      this._loadData();
    }
  };

  @action
  private _onOnlineChange = () => {
    this._loadData();
  };

  private _onUploadDone = () => {
    this._loadData();
  };

  private _onPopState = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const folderId = urlParams.get('folder');
    runInAction(() => {
      if (window.location.pathname === '/trash') {
        this._page = 'trash';
        this._trashedFiles = null;
        this._files = null;
      } else if (!folderId) {
        this._page = 'files';
        this._currentFolderId = null;
        this._folderPath = [];
        this._files = null;
      } else {
        this._page = 'files';
        this._currentFolderId = folderId;
        this._folderPath = [{ id: folderId, name: 'Folder' }];
        this._files = null;
      }
    });
    this._loadData();
  };

  @action.bound
  private _onToggleSidebar() {
    this._sidebarOpen = !this._sidebarOpen;
  }

  @action.bound
  private _onCloseSidebar() {
    this._sidebarOpen = false;
  }

  private _onFileDeleted = () => {
    // Skip refresh if a deletion animation is in progress — the deletion flow
    // itself will refresh after the animation completes.
    if (this._removingIds.size === 0) {
      this._loadData();
    }
  };

  private async _loadData() {
    if (this._page === 'trash') {
      const files = await apiService.get<FileResponseItem[]>('/api/file/trash');
      runInAction(() => { this._trashedFiles = files; });
      return;
    }
    //load file list
    const folderId = this._currentFolderId;
    const userIdOverride = new URLSearchParams(window.location.search).get('userId');
    const userIdParam = userIdOverride ? `&userId=${encodeURIComponent(userIdOverride)}` : '';
    const url = folderId
      ? `/api/file?folderId=${folderId}${userIdParam}`
      : userIdOverride ? `/api/file?userId=${encodeURIComponent(userIdOverride)}` : '/api/file';
    const foldersUrl = userIdOverride
      ? `/api/file/folders?userId=${encodeURIComponent(userIdOverride)}`
      : null;
    const needFolders = foldersUrl !== null || folderStore.folders === null;
    const [files, storageInfo, allFolders] = await Promise.all([
      apiService.get<FileResponseItem[]>(url),
      apiService.get<{ usedSpace: number; storageLimit: number; filesCount?: number; trashUsedSpace?: number }>('/api/user/dashboard'),
      needFolders
        ? apiService.get<FileResponseItem[]>(foldersUrl ?? '/api/file/folders')
        : Promise.resolve(folderStore.folders!),
    ]);
    if (!userIdOverride) {
      folderStore.setFolders(allFolders);
    }
    runInAction(() => {
      this._files = files;
      this._storageInfo = storageInfo;
      if (this._currentFolderId && allFolders.length > 0) {
        this._folderPath = this._buildPathToFolder(this._currentFolderId, allFolders);
      }
    });
  }

  render(){
    const breadcrumb = (
      <>
        {this._folderPath.map((folder, i) => (
          <React.Fragment key={folder.id}>
            <span>›</span>
            {i < this._folderPath.length - 1
              ? <a style={{ cursor: 'pointer' }} onClick={() => this._onNavigateToBreadcrumb(i)}>{folder.name}</a>
              : <span>{folder.name}</span>
            }
          </React.Fragment>
        ))}
      </>
    );

    return (
      <>
        <div id="mainApp" className="app-container">
          <PageHeader
            breadcrumb={breadcrumb}
            storageInfo={this._storageInfo}
            onLogoClick={this._onGoToRoot}
            onSidebarToggle={this._onToggleSidebar}
          />

          <div className="main-content">
              <Sidebar
                activeItem={this._page}
                isOpen={this._sidebarOpen}
                currentFolderId={this._currentFolderId}
                onGoToRoot={this._onGoToRoot}
                onShowTrash={this._onShowTrash}
                onFolderClick={this._onSidebarFolderClick}
                onDelete={(f) => this._onDeleteFile(f)}
                onPermanentDelete={(f) => this._onPermanentlyDeleteFile(f)}
                onManageExpiration={(f) => this._onManageExpiration(f)}
              />
              {this._sidebarOpen && (
                <div className="sidebar-overlay" onClick={this._onCloseSidebar} />
              )}

              <main className="content-area">
                  {this._page === 'files' ? (
                  <>
                      <div className="toolbar">
                          <div className="toolbar-left">
                              <div className="split-btn-group">
                                <button className="btn btn-primary split-btn-main" onClick={this._onUploadClick}>
                                  <ImgUpload /> Upload
                                </button>
                                <button ref={this._uploadArrowRef} className="btn btn-primary split-btn-arrow" onClick={(e) => e.stopPropagation()} title="More upload options">
                                  <ImgChevronDown />
                                </button>
                              </div>
                              <ContextMenu container={this._uploadArrowRef} showOnClick>
                                {() => (
                                  <div>
                                    <ContextMenuItem title={<><ImgUpload /> Upload Files</>} onClick={this._onUploadClick} />
                                    <ContextMenuItem title={<><ImgLink /> Upload from URL</>} onClick={this._onUploadUrlClick} />
                                  </div>
                                )}
                              </ContextMenu>
                              <button className="btn btn-secondary view-toggle-btn" onClick={this._onToggleViewClick}>
                                {this._viewMode === 'grid' ? <ImgViewGrid /> : <ImgViewList />} View
                              </button>
                              <button className="btn btn-secondary toolbar-icon-btn search-toggle-btn" onClick={this._onToggleSearchClick} title={this._searchOpen ? 'Hide search' : 'Search files'} aria-label={this._searchOpen ? 'Hide search' : 'Search files'}>
                                <ImgSearch />
                              </button>
                          </div>
                          <div className={'search-box' + (this._searchOpen ? ' open' : '')}>
                              <span className="search-icon"><ImgSearch /></span>
                              <input ref={this._searchInputRef} type="text" placeholder="Search files..." value={this._searchTerm} onChange={this._onSearchChange} />
                          </div>
                          <div className="toolbar-right">
                              <button ref={this._moreMenuRef} className="btn btn-secondary toolbar-icon-btn" onClick={(e) => e.stopPropagation()} title="More actions" aria-label="More actions">
                                <ImgThreeDots />
                              </button>
                              <ContextMenu container={this._moreMenuRef} showOnClick align="right">
                                {() => (
                                  <div>
                                    <ContextMenuItem title={<><ImgFolderAdd /> New Folder</>} onClick={this._onNewFolderClick} />
                                    <ContextMenuItem title={<><ImgLink /> Create Shared Space</>} onClick={this._onCreateShareSpaceClick} />
                                    <ContextMenuItem title={<>{this._viewMode === 'grid' ? <ImgViewGrid /> : <ImgViewList />} {this._viewMode === 'grid' ? 'List View' : 'Grid View'}</>} onClick={this._onToggleViewClick} />
                                    <ContextMenuItem title={<><ImgHashtag /> Enter Code</>} onClick={this._onEnterCodeClick} />
                                  </div>
                                )}
                              </ContextMenu>
                          </div>
                      </div>

                      {this._renderFilesList()}
                  </>
                  ) : (
                  <div id="trashPage">
                      <div className="toolbar">
                          <div className="toolbar-left">
                              <span style={{ fontWeight: 600, fontSize: '16px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}><ImgTrash /> Trash</span>
                              <span style={{ marginLeft: '12px', fontSize: '13px', opacity: 0.7 }}>Files are permanently deleted 1 month after being trashed.</span>
                          </div>
                      </div>
                      {this._renderTrashView()}
                  </div>
                  )}

              </main>
          </div>
      </div>
      <UploadPanel ref={this._uploadPanelRef} folderId={this._currentFolderId} enabled={this._page === 'files'} />
      <input
        ref={this._reuploadFileRef}
        type="file"
        style={{ display: 'none' }}
        onChange={this._onReuploadFileChosen}
      />
      </>
    );
  }

  private _onUploadClick = () => {
    this._uploadPanelRef.current?.triggerFileInput();
  };

  @action.bound
  private _onToggleSearchClick() {
    this._searchOpen = !this._searchOpen;
    if (this._searchOpen) {
      // Defer focus until the input is visible after the re-render
      setTimeout(() => this._searchInputRef.current?.focus(), 0);
    }
  }

  @action.bound
  private _onShowTrash() {
    this._page = 'trash';
    this._trashedFiles = null;
    window.history.pushState(null, '', '/trash');
    this._loadData();
  }

  private async _onNewFolderClick() {
    const result = await modalService.show<{}, string>(CreateFolderDialog);
    if (result.button === ModalButtonType.Add && result.result) {
      await apiService.post('/api/file/folder', { folderName: result.result, parentFolderId: this._currentFolderId });
      folderStore.reload();
      await this._loadData();
    }
  };

  private _onCreateShareSpaceClick = async () => {
    const result = await modalService.show<{}, { name: string; sizeGb: number }>(CreateShareSpaceDialog);
    if (result.button !== ModalButtonType.Add || !result.result) {
      return;
    }
    const created = await apiService.post<{ link: string }>('/api/file/share-space', {
      name: result.result.name,
      sizeGb: result.result.sizeGb,
    });
    window.location.href = created.link;
  };

  private _buildPathToFolder(folderId: string, folders?: FileResponseItem[]): { id: string; name: string }[] {
    const allFolders = folders ?? folderStore.folders ?? [];
    const path: { id: string; name: string }[] = [];
    let current: string | null = folderId;
    while (current) {
      const folder = allFolders.find(f => f.id === current);
      if (!folder) break;
      path.unshift({ id: folder.id, name: folder.fileName });
      current = folder.parentFolderId ?? null;
    }
    return path;
  }

  @action.bound
  private _onSidebarFolderClick(folderId: string) {
    this._page = 'files';
    this._folderPath = this._buildPathToFolder(folderId);
    if (this._folderPath.length === 0) {
      const folder = (folderStore.folders ?? []).find(f => f.id === folderId);
      if (folder) this._folderPath = [{ id: folder.id, name: folder.fileName }];
    }
    this._currentFolderId = folderId;
    this._files = null;
    window.history.pushState(null, '', `/?folder=${folderId}`);
    this._loadData();
  }

  @action.bound
  private _onGoToRoot() {
    const hasUserId = !!new URLSearchParams(window.location.search).get('userId');
    // Skip if already at root with data loaded — avoids visual flicker
    if (this._page === 'files' && this._currentFolderId === null && this._folderPath.length === 0 && this._files !== null && !hasUserId) {
      return;
    }
    this._page = 'files';
    this._folderPath = [];
    this._currentFolderId = null;
    this._files = null;
    window.history.pushState(null, '', '/');
    this._loadData();
  }

  @action.bound
  private _onNavigateToBreadcrumb(index: number) {
    const target = this._folderPath[index];
    this._page = 'files';
    this._folderPath = this._folderPath.slice(0, index + 1);
    this._currentFolderId = target.id;
    this._files = null;
    window.history.pushState(null, '', `/?folder=${target.id}`);
    this._loadData();
  }

  private _onUploadUrlClick = () => {
    modalService.show(UrlUploadDialog, { parentFolderId: this._currentFolderId });
  };

  private _onEnterCodeClick = () => {
    modalService.show(EnterCodeDialog);
  };

  private _renderFilesList() {
    const files = this.filteredFiles;
    if (files === null) {
      return <div>Loading files...</div>;
    }
    const activeUploads = this._getActiveUploadsForCurrentFolder();
    const activeUploadIds = new Set(activeUploads.map(u => u.id));
    // Exclude files that are already shown as live upload rows above
    const displayFiles = files.filter(f => !activeUploadIds.has(f.id));
    if (this._viewMode === 'grid') {
      return (
        <FileGridView
          files={displayFiles}
          activeUploads={activeUploads}
          removingIds={this._removingIds}
          renderText={this._renderText}
          onPreviewFile={this._onPreviewFile}
          onDeleteFile={(f) => this._onDeleteFile(f)}
          onPermanentDelete={(f) => this._onPermanentlyDeleteFile(f)}
          onManageExpiration={(f) => this._onManageExpiration(f)}
          onCustomize={(f) => this._onCustomizeFile(f)}
          onShareCode={(f) => this._onShareCode(f)}
          onReupload={(f) => this._onReuploadFile(f)}
          onCustomLink={(f) => this._onCustomLinkFile(f)}
        />
      );
    } else if (this._viewMode === 'list') {
      return (
        <FileListView
          files={displayFiles}
          activeUploads={activeUploads}
          removingIds={this._removingIds}
          renderText={this._renderText}
          onPreviewFile={this._onPreviewFile}
          onDeleteFile={(f) => this._onDeleteFile(f)}
          onPermanentDelete={(f) => this._onPermanentlyDeleteFile(f)}
          onManageExpiration={(f) => this._onManageExpiration(f)}
          onCustomize={(f) => this._onCustomizeFile(f)}
          onShareCode={(f) => this._onShareCode(f)}
          onReupload={(f) => this._onReuploadFile(f)}
          onCustomLink={(f) => this._onCustomLinkFile(f)}
        />
      );
    } else if (this._viewMode === 'cards') {
      // Render files in cards view
    }
  }

  private _getActiveUploadsForCurrentFolder() {
    return uploadService.uploads.filter(u => {
      if (u.status === 'done' || u.status === 'error') return false;
      if ((u.parentFolderId ?? null) !== this._currentFolderId) return false;
      // Only hide the upload row if the file already appears as complete in the list.
      // If it's incomplete (still uploading), keep showing the live upload row instead.
      if (this._files?.some(f => f.id === u.id && f.isComplete)) return false;
      return true;
    });
  }

  private _onPreviewFile = async (file: FileResponseItem) => {
    const type = getPreviewableType(file.fileName);
    const sharePath = file.customName ? `/f/${file.customName}` : `/file/download/${file.id}`;
    if (type === 'pdf') {
      await modalService.show(PdfViewerDialog, { fileId: file.id, filename: file.fileName, sharePath });
    } else if (type === 'text') {
      await modalService.show(TextViewerDialog, { fileId: file.id, filename: file.fileName, sharePath });
    } else if (type === 'video' || type === 'audio') {
      await modalService.show(MediaPlayerDialog, { fileId: file.id, filename: file.fileName, mediaType: type });
    } else if (type === 'zip') {
      await modalService.show(ZipViewerDialog, { fileId: file.id, filename: file.fileName });
    }
  };

  @action.bound
  private _onSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    this._searchTerm = event.target.value;
  }
  
  @computed
  get searchTerms(){
      const searchStr = this._searchTerm;
      return searchStr.split(/[ ,]+/).filter(x => !!x);
  }
  
  private _renderText = (text: string | undefined | null) => {
    const { searchTerms } = this;

    const parts = stringSearchService.markMatches(text, searchTerms, defaulComparator);
    return (<>
      {parts.map((p, index) => p.isMatch ? <span className="text-match" key={'t' + index}>{p.text}</span> : p.text)}
    </>);
  };

  @computed
  private get filteredFiles(): FileResponseItem[] | null {
    if (this._files === null) return null;
    let filteredList = this._files;
    
    // Apply search filter
    const searchStringParts = this.searchTerms;
    if (searchStringParts.length) {
      filteredList = filteredList.filter((file): boolean => {
        let result = true;

        for(const term of searchStringParts){
          let anyMatch = false;

          if (defaulComparator(file.fileName, term, 0) !== -1){
              anyMatch = true;
          }

          //if (file.keywords && file.keywords.length > 0 && defaulComparator(file.keywords.join(' '), term, 0) !== -1){
          //  anyMatch = true;
          //}

          if (!anyMatch){
              result = false;
          }
        }

        return result;
      });
    }
    
    return filteredList;
  }

  @action.bound
  private _onToggleViewClick() {
    if (this._viewMode === 'grid') {
      this._viewMode = 'list';
    } else if (this._viewMode === 'list') {
      this._viewMode = 'grid';
    }
    localStorage.setItem('viewMode', this._viewMode);
  }

  private async _onDeleteFile(file: FileResponseItem) {
    const isConfirmed = await modalService.showConfirmation(<span>Move <b>{file.fileName}</b> to trash?</span>, 'Move to Trash', {
      onDone: async () => {
        await apiService.delete(`/api/file/${file.id}`);
      }
    });
    
    if (!isConfirmed) {
      return;
    }
    runInAction(() => this._removingIds.add(file.id));
    await new Promise(resolve => setTimeout(resolve, 500));
    await this._loadData();
    if (file.isFolder) await folderStore.reload();
    runInAction(() => this._removingIds.delete(file.id));
  }

  private async _onRestoreFile(file: FileResponseItem) {
    await apiService.post(`/api/file/${file.id}/restore`, {});
    await this._loadData();
  }

  private async _onPermanentlyDeleteFile(file: FileResponseItem) {
    const isConfirmed = await modalService.showConfirmation(
      <span>Permanently delete <b>{file.fileName}</b>? This cannot be undone.</span>,
      'Delete Permanently',
      {
        onDone: async () => {
          await apiService.delete(`/api/file/${file.id}/permanent`);
        }
      }
    );
    if (!isConfirmed) {
      return;
    }
    runInAction(() => this._removingIds.add(file.id));
    await new Promise(resolve => setTimeout(resolve, 500));
    await this._loadData();
    if (file.isFolder) await folderStore.reload();
    runInAction(() => this._removingIds.delete(file.id));
  }

  private async _onShareCode(file: FileResponseItem) {
    await modalService.show<{ fileId: string; fileName: string }, void>(AccessCodeDialog, { fileId: file.id, fileName: file.fileName });
  }

  private async _onCustomLinkFile(file: FileResponseItem) {
    const result = await modalService.show<{ currentCustomName: string | null }, import('./dialogs/CustomLinkDialog.tsx').CustomLinkResult>(
      CustomLinkDialog,
      { currentCustomName: file.customName ?? null }
    );
    if (result.button === ModalButtonType.Add || result.button === ModalButtonType.Remove) {
      const newCustomName = result.result?.customName ?? null;
      await apiService.put(`/api/file/${file.id}/customName`, { customName: newCustomName });
      if (this._files) {
        const f = this._files.find(f => f.id === file.id);
        if (f) f.customName = newCustomName;
      }
    }
  }

  private async _onReuploadFile(file: FileResponseItem) {
    const isConfirmed = await modalService.showConfirmation(
      <span>Replace the content of <b>{file.fileName}</b>? The file will go back to “incomplete” until the new upload finishes. Existing links keep the same address.</span>,
      'Re-upload File',
      { buttonTitle: 'Replace' }
    );
    if (!isConfirmed) return;
    this._reuploadTarget = file;
    this._reuploadFileRef.current?.click();
  }

  private async _onReuploadFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const input = e.target;
    const file = input.files?.[0];
    const target = this._reuploadTarget;
    input.value = '';
    this._reuploadTarget = null;
    if (!file || !target) return;
    await uploadService.refreshFile(target.id, file, target.parentFolderId ?? null);
  }

  private async _onCustomizeFile(file: FileResponseItem) {
    const result = await modalService.show<{ currentSettings?: ShareSettings }, ShareSettings>(
      CustomizeShareDialog,
      { currentSettings: file.shareSettings }
    );
    if (result.button === ModalButtonType.Add && result.result != null) {
      await apiService.put(`/api/file/${file.id}/share-settings`, result.result);
      if (this._files) {
        const f = this._files.find(f => f.id === file.id);
        if (f) f.shareSettings = result.result;
      }
    }
  }

  private async _onManageExpiration(file: FileResponseItem) {
    const result = await modalService.show<{ currentExpiration: string | null }, import('./dialogs/SetExpirationDialog.tsx').SetExpirationResult>(
      SetExpirationDialog,
      { currentExpiration: file.expirationDateTime ?? null }
    );
    if (result.button === ModalButtonType.Add && result.result != null) {
      await apiService.put(`/api/file/${file.id}/expiration`, { expirationDate: result.result.expirationDate });
      // Update only the specific file's expiration date in the existing list
      if (this._files) {
        const fileIndex = this._files.findIndex(f => f.id === file.id);
        if (fileIndex !== -1) {
          this._files[fileIndex].expirationDateTime = result.result.expirationDate ?? null;
        }
      }
    }
  }

  private _renderTrashView() {
    return (
      <TrashView
        files={this._trashedFiles}
        removingIds={this._removingIds}
        onRestore={(file) => this._onRestoreFile(file)}
        onDelete={(file) => this._onPermanentlyDeleteFile(file)}
      />
    );
  }
}

export default Home;